/**
 * Error Status Utilities
 *
 * Leaf-level helpers for error classification and status-based messaging.
 * Extracted to break circular dependency between error.ts and error-handler.ts.
 */

/**
 * Check if error is an abort/cancellation (user or system initiated).
 * Uses error.name (standard abort detection) with 'aborted' message fallback.
 * Intentionally narrow to avoid suppressing legitimate errors containing words
 * like 'stop', 'cancel', or 'interrupt' (e.g. "Cannot stop the operation").
 */
export function isAbortError(error: Error) {
    return error.name === 'AbortError' ||
           error.message?.toLowerCase().includes('aborted');
}

/**
 * Get user-friendly error message based on HTTP status code
 * Used globally across all error handling to provide consistent, actionable messages
 *
 * @param status - HTTP status code
 * @param defaultMessage - Fallback message if status doesn't match known patterns
 * @returns User-friendly error message
 */
export function getStatusBasedMessage(status: number, defaultMessage?: string): string {
    switch (status) {
    case 401:
        return 'Your session has expired. Please refresh the page to log in again.';
    case 403:
        return 'You do not have permission to access this resource. Please contact your administrator.';
    case 404:
        return 'The requested resource was not found. It may have been moved or deleted.';
    case 408:
        return 'The request timed out. Please try again.';
    case 429:
        return 'Too many requests. Please wait a moment and try again.';
    case 500:
        return 'A server error occurred. Please try again later.';
    case 502:
    case 503:
        return 'The service is temporarily unavailable. Please try again in a few moments.';
    case 504:
        return 'The server took too long to respond. Please try again.';
    default:
        if (status >= 500 && status < 600) {
            return 'A server error occurred. Please try again later.';
        }
        if (status >= 400 && status < 500) {
            return defaultMessage || 'The request could not be completed. Please check your input and try again.';
        }
        return defaultMessage || 'An unexpected error occurred. Please try again.';
    }
}
